import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Input,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Link as JoyLink,
  LinearProgress,
} from '@mui/joy'
import {
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  ErrorOutline,
} from '@mui/icons-material'
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom'
import { authService } from '@/services/authService'

type PageState = 'verifying' | 'form' | 'submitting' | 'success' | 'invalid'

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/

function getPasswordStrength(password: string): number {
  if (!password) return 0
  let score = 0
  if (password.length >= 8) score += 25
  if (/[a-z]/.test(password)) score += 25
  if (/[A-Z]/.test(password)) score += 25
  if (/\d/.test(password)) score += 25
  return score
}

function getStrengthColor(score: number): 'danger' | 'warning' | 'success' | 'neutral' {
  if (score <= 25) return 'danger'
  if (score <= 50) return 'warning'
  if (score <= 75) return 'warning'
  return 'success'
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [pageState, setPageState] = useState<PageState>('verifying')
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; server?: string }>({})

  const verifyToken = useCallback(async () => {
    if (!token) {
      navigate('/forgot-password', { replace: true })
      return
    }

    try {
      const result = await authService.verifyResetToken(token)
      if (result.valid && result.email) {
        setEmail(result.email)
        setPageState('form')
      } else {
        setPageState('invalid')
      }
    } catch {
      setPageState('invalid')
    }
  }, [token, navigate])

  useEffect(() => {
    verifyToken()
  }, [verifyToken])

  const validate = (): boolean => {
    const newErrors: typeof errors = {}

    if (!PASSWORD_REGEX.test(newPassword)) {
      newErrors.password =
        'At least 8 characters, including uppercase, lowercase, and a number.'
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirm = 'Passwords do not match.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate() || !token) return

    setPageState('submitting')
    setErrors({})

    try {
      await authService.setupPassword(token, newPassword)
      setPageState('success')
      setTimeout(() => navigate('/login', { replace: true }), 3000)
    } catch (err: any) {
      setPageState('form')
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.'
      if (msg.includes('invalid') || msg.includes('expired')) {
        setPageState('invalid')
      } else {
        setErrors({ server: msg })
      }
    }
  }

  const strengthScore = getPasswordStrength(newPassword)
  const strengthColor = getStrengthColor(strengthScore)

  if (pageState === 'verifying') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.level1',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography level="body-md" color="neutral">
            Verifying your reset link...
          </Typography>
        </Stack>
      </Box>
    )
  }

  if (pageState === 'invalid') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.level1',
          p: 2,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <ErrorOutline sx={{ fontSize: 48, color: 'danger.500' }} />
              <Typography level="h3">Link invalid or expired</Typography>
              <Typography level="body-md" color="neutral">
                This reset link is invalid or has expired.
              </Typography>
              <Button
                component={RouterLink}
                to="/forgot-password"
                fullWidth
              >
                Request a new link
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (pageState === 'success') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.level1',
          p: 2,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <CheckCircle sx={{ fontSize: 48, color: 'success.500' }} />
              <Typography level="h3">Password reset!</Typography>
              <Typography level="body-md" color="neutral">
                Your password has been reset. You can now log in.
              </Typography>
              <Typography level="body-sm" color="neutral">
                Redirecting to login...
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
              >
                Go to login
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  const isSubmitting = pageState === 'submitting'

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.level1',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent>
          <Box textAlign="center" mb={4}>
            <Typography level="h2" fontWeight="lg" mb={1}>
              Set a new password
            </Typography>
            <Typography level="body-sm" color="neutral">
              Resetting password for <strong>{email}</strong>
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {errors.server && (
                <Alert color="danger" variant="soft">
                  {errors.server}
                </Alert>
              )}

              <FormControl error={!!errors.password}>
                <Typography level="body-sm" mb={1}>
                  New Password
                </Typography>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    if (errors.password) setErrors((p) => ({ ...p, password: undefined }))
                  }}
                  error={!!errors.password}
                  disabled={isSubmitting}
                  size="lg"
                  startDecorator={<Lock />}
                  endDecorator={
                    <Button
                      variant="plain"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isSubmitting}
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  }
                />
                {errors.password && <FormHelperText>{errors.password}</FormHelperText>}
              </FormControl>

              {newPassword && (
                <Box>
                  <LinearProgress
                    determinate
                    value={strengthScore}
                    color={strengthColor}
                    sx={{ mb: 0.5 }}
                  />
                  <Typography level="body-xs" color="neutral">
                    At least 8 characters, including uppercase, lowercase, and a number.
                  </Typography>
                </Box>
              )}

              <FormControl error={!!errors.confirm}>
                <Typography level="body-sm" mb={1}>
                  Confirm New Password
                </Typography>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (errors.confirm) setErrors((p) => ({ ...p, confirm: undefined }))
                  }}
                  error={!!errors.confirm}
                  disabled={isSubmitting}
                  size="lg"
                  startDecorator={<Lock />}
                  endDecorator={
                    <Button
                      variant="plain"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isSubmitting}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  }
                />
                {errors.confirm && <FormHelperText>{errors.confirm}</FormHelperText>}
              </FormControl>

              <Button
                type="submit"
                size="lg"
                fullWidth
                disabled={isSubmitting}
                startDecorator={isSubmitting ? <CircularProgress size="sm" /> : undefined}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}
